import React, { useState, useEffect } from 'react'
import Topbar from '../layouts/Topbar'
import Sidebar from '../layouts/Sidebar'
import '../../Employee/screens/ImprovementPlans.css'
import { toast } from 'react-toastify'
import axios from 'axios'
import Cookies from 'js-cookie'
import { useNavigate } from 'react-router-dom'
import API_BASE_URL from '../../../config/api'

const COOImprovementPlan = () => {
  const [improvementPlans, setImprovementPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const baseUrl = API_BASE_URL;
  const navigate = useNavigate();

  useEffect(() => {
    fetchImprovementPlans();
  }, []);

  // No filtering – show all plans as cards

  const fetchImprovementPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = Cookies.get('token');
      const response = await axios.get(`${baseUrl}/api/users/me/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const plans = Array.isArray(response.data.employee.improvement_plans) ? response.data.employee.improvement_plans : [];
      setImprovementPlans(plans);

      // Showing all plans as cards; no year filtering

    } catch (err) {
      console.error('Error fetching improvement plans:', err);
      setError('Failed to fetch improvement plans');
      toast.error('Failed to fetch improvement plans');
    } finally {
      setLoading(false);
    }
  };

  const openDetails = (plan) => {
    setSelectedPlan(plan);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setSelectedPlan(null);
  };

  const handleEditPlan = () => {
    navigate(`/coo/edit-improvement-plan/${selectedPlan.id}`);
    
  };

  const handleDeletePlan = () => setDeleteModalOpen(true);

  const confirmDelete = async () => {
    try {
      const token = Cookies.get('token');
      if (!selectedPlan?.id) throw new Error('No plan selected');
      await axios.delete(`${baseUrl}/api/improvement-plans/${selectedPlan.id}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      toast.success('Improvement plan deleted successfully');
      setDeleteModalOpen(false);
      setDetailsOpen(false);
      setSelectedPlan(null);
      fetchImprovementPlans();
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error('Failed to delete improvement plan');
    }
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const splitToList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    return String(value)
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0);
  };

  return (
    <>
      <Topbar />
      <Sidebar />
      <div className="employee-goals-container">
        <div className="page-header">
          <div className="header-content">
            <div className="page-title">
              <h1>Improvement Plans</h1>
              <p>Browse your improvement plans</p>
            </div>
            <div className="header-actions"></div>
          </div>
        </div>

        <div style={{ padding: '20px' }} className="wrapper">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner" />
              <span>Loading improvement plans...</span>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
            </div>
          ) : (
            <div className="plans-grid">
              {improvementPlans.length === 0 ? (
                <div className="no-data-state" style={{ gridColumn: '1/-1' }}>
                  <h3>No Improvement Plans Found</h3>
                  <p>You can create a new improvement plan for this year.</p>
                </div>
              ) : (
                improvementPlans.map((plan) => (
                  <div key={plan.id} className="plan-card" onClick={() => openDetails(plan)} role="button" tabIndex={0} onKeyPress={() => openDetails(plan)}>
                    <div className="card-header">
                      <div className="card-title">Improvement Plan {plan.year || ''}</div>
                      <span style={{ marginRight: '20px' }} className={`status-badge ${plan.approval_status === 'approved' ? 'badge-success' : plan.approval_status === 'rejected' ? 'badge-danger' : 'badge-warning'}`}>
                        {plan.approval_status || 'pending'}
                      </span>
                    </div>
                    <div className="card-meta">Created: {formatDate(plan.date_created)}</div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {detailsOpen && selectedPlan && (
        <div className="modal-overlay" onClick={closeDetails}>
          <div className="modal-content plan-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-wrap">
                <h3>Improvement Plan {selectedPlan.year}</h3>
                <span className={`status-badge ${selectedPlan.approval_status === 'approved' ? 'badge-success' : selectedPlan.approval_status === 'rejected' ? 'badge-danger' : 'badge-warning'}`}>{selectedPlan.approval_status || 'pending'}</span>
              </div>
              <button className="modal-close" onClick={closeDetails} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <div className="plan-meta" style={{ marginBottom: 12 }}>
                <span className="plan-date">Created: {formatDate(selectedPlan.date_created)}</span>
              </div>
              <div className="modal-sections-grid">
                <div className="plan-section">
                  <div className="section-header"><h3>Strengths</h3></div>
                  <div className="section-content">
                    {splitToList(selectedPlan.strengths).length > 0 ? (
                      <ul className="bullet-list">
                        {splitToList(selectedPlan.strengths).map((item, idx) => (
                          <li key={`strength-${idx}`}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>No strengths specified</p>
                    )}
                  </div>
                </div>
                <div className="plan-section">
                  <div className="section-header"><h3>Areas for Improvement</h3></div>
                  <div className="section-content">
                    {splitToList(selectedPlan.weaknesses).length > 0 ? (
                      <ul className="bullet-list">
                        {splitToList(selectedPlan.weaknesses).map((item, idx) => (
                          <li key={`weakness-${idx}`}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>No weaknesses specified</p>
                    )}
                  </div>
                </div>
                <div className="plan-section">
                  <div className="section-header"><h3>Training Needs</h3></div>
                  <div className="section-content">
                    {splitToList(selectedPlan.training_needs).length > 0 ? (
                      <ul className="bullet-list">
                        {splitToList(selectedPlan.training_needs).map((item, idx) => (
                          <li key={`training-${idx}`}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>No training needs specified</p>
                    )}
                  </div>
                </div>
                <div className="plan-section">
                  <div className="section-header"><h3>Employee Comment</h3></div>
                  <div className="section-content"><p className="comment-block">{selectedPlan.employee_comment || 'No comment provided'}</p></div>
                </div>
              </div>
              {selectedPlan.approval_status === 'approved' && (
                <div className="plan-section">
                  <div className="section-header"><h3>Supervisor Feedback</h3></div>
                  <div className="section-content"><p>{selectedPlan.supervisor_comment || 'No supervisor comment provided'}</p></div>
                </div>
              )}

              <div className="modal-actions" style={{ justifyContent: 'flex-end' }}>
                {(selectedPlan.approval_status === 'pending' || selectedPlan.approval_status === 'rejected') && (
                  <>
                    <button onClick={handleEditPlan} className="btn btn-primary">Edit</button>
                    <button onClick={handleDeletePlan} className="btn btn-danger">Delete</button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Improvement Plan</h3>
              <button className="modal-close" onClick={closeDeleteModal} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <div className="delete-warning">
                <p>Are you sure you want to delete this improvement plan for {selectedPlan?.year}?</p>
                <p className="warning-text">This action cannot be undone.</p>
              </div>
              <div className="modal-actions">
                <button onClick={closeDeleteModal} className="btn btn-secondary">Cancel</button>
                <button onClick={confirmDelete} className="btn btn-danger">Delete Plan</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default COOImprovementPlan